import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../../utils/axios';
import { toJstDateInputValue } from '../../utils/dateTimeJst';
import AlertModal from '../common/AlertModal';
import './doctor.scss';

const NextAppointmentModal = ({
  open,
  onClose,
  sessionId,
  patientName,
  primaryDiseaseId,
  primaryDiseaseName,
  primaryDiseaseInsuranceCovered,
}) => {
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');
  const [diseases, setDiseases] = useState([]);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState(primaryDiseaseId ? Number(primaryDiseaseId) : null);
  const [selectedDiseaseName, setSelectedDiseaseName] = useState(primaryDiseaseName || '');
  const [selectedDiseaseInsuranceCovered, setSelectedDiseaseInsuranceCovered] = useState(
    typeof primaryDiseaseInsuranceCovered === 'boolean' ? primaryDiseaseInsuranceCovered : null
  );
  const [searchDate, setSearchDate] = useState('');
  const [searchTimeFrom, setSearchTimeFrom] = useState('10:00');
  const [searchTimeTo, setSearchTimeTo] = useState('18:00');
  
  const [doctors, setDoctors] = useState([]);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedDoctorName, setSelectedDoctorName] = useState('');
  const [showOtherDoctors, setShowOtherDoctors] = useState(false); // 自分以外の医師トグル
  
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set()); // アコーディオン開閉状態
  const [hasSearched, setHasSearched] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false); // 手動入力トグル
  const [manualDate, setManualDate] = useState('');
  const [manualTime, setManualTime] = useState('10:00');

  // AlertModal用のstate
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    type: 'alert',
    onConfirm: null
  });

  // AlertModalを閉じる
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, open: false }));
  };

  // アラートを表示
  const showAlert = (title, message, iconType = null) => {
    setAlertModal({
      open: true,
      title,
      message,
      type: 'alert',
      iconType,
      onConfirm: null
    });
  };

  // 確認ダイアログを表示
  const showConfirm = (title, message, onConfirm) => {
    setAlertModal({
      open: true,
      title,
      message,
      type: 'confirm',
      iconType: 'confirm',
      onConfirm
    });
  };

  // 初期データ読み込み
  useEffect(() => {
    if (!open || !sessionId) return;
    
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // 診療科目マスターを取得
        const specialtiesRes = await axiosInstance.get('/api/medichat_doctor/masters/specialties');
        const specList = specialtiesRes.data.specialties || [];
        setSpecialties(specList);

        const diseasesRes = await axiosInstance.get('/api/medichat_doctor/masters/diseases');
        const diseaseList = Array.isArray(diseasesRes.data?.diseases)
          ? diseasesRes.data.diseases
          : (Array.isArray(diseasesRes.data?.items) ? diseasesRes.data.items : []);
        setDiseases(diseaseList);
        
        // セッション情報を取得して、デフォルトの診療科目と担当医を設定
        const sessionRes = await axiosInstance.get(`/api/medichat_doctor/post_consult_reviews/${sessionId}`);
        const sessionData = sessionRes.data;

        const effectiveDiseaseId = Number(primaryDiseaseId || sessionData.primary_disease_id || 0) || null;
        let resolvedSpecialtyId = '';
        if (effectiveDiseaseId) {
          const selected = diseaseList.find(d => Number(d.id) === effectiveDiseaseId);
          if (selected) {
            setSelectedDiseaseId(effectiveDiseaseId);
            setSelectedDiseaseName(selected.disease_name || selected.name || '');
            setSelectedDiseaseInsuranceCovered(!!selected.insurance_covered);
            if (selected.specialty_id) {
              resolvedSpecialtyId = String(selected.specialty_id);
              setSelectedSpecialtyId(resolvedSpecialtyId);
            }
          }
        }
        
        // デフォルトの診療科目を設定
        // 疾患未設定時のみ先頭の診療科をデフォルトにする
        if (!resolvedSpecialtyId && specList.length > 0) {
          setSelectedSpecialtyId(String(specList[0].id));
        }
        const today = toJstDateInputValue();
        setSearchDate(prev => prev || today);
        setSearchTimeFrom(prev => prev || '10:00');
        setSearchTimeTo(prev => prev || '18:00');
        
        // 担当医情報を取得
        const doctorsRes = await axiosInstance.get(
          `/api/medichat_doctor/post_consult_reviews/${sessionId}/next_appointment_options`,
          { params: effectiveDiseaseId ? { disease_id: effectiveDiseaseId } : undefined }
        );
        const doctorsData = doctorsRes.data;
        
        setCurrentDoctor(doctorsData.current_doctor);
        setDoctors(doctorsData.doctors || []);
        
        if (doctorsData.current_doctor) {
          setSelectedDoctorId(doctorsData.current_doctor.id);
          setSelectedDoctorName(doctorsData.current_doctor.name);
          setShifts(doctorsData.default_shifts || []);
        }
      } catch (error) {
        console.error('初期データ読み込みエラー:', error);
        showAlert('エラー', 'データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [open, sessionId, primaryDiseaseId]);

  useEffect(() => {
    if (!open) return;
    if (!primaryDiseaseId) return;
    setSelectedDiseaseId(Number(primaryDiseaseId));
    if (primaryDiseaseName) setSelectedDiseaseName(primaryDiseaseName);
    if (typeof primaryDiseaseInsuranceCovered === 'boolean') {
      setSelectedDiseaseInsuranceCovered(primaryDiseaseInsuranceCovered);
    }
  }, [open, primaryDiseaseId, primaryDiseaseName, primaryDiseaseInsuranceCovered]);

  const firstShiftDate = shifts[0]?.date;
  useEffect(() => {
    if (!open || !firstShiftDate) return;
    setExpandedDates(new Set([firstShiftDate]));
  }, [open, firstShiftDate, selectedDoctorId]);

  // 医師を選択
  const selectDoctor = async (doctorId, doctorName) => {
    setSelectedDoctorId(doctorId);
    setSelectedDoctorName(doctorName);
    
    try {
      const res = await axiosInstance.get(`/api/medichat_doctor/post_consult_reviews/${sessionId}/next_appointment_options`, {
        params: { doctor_id: doctorId }
      });
      setShifts(res.data.shifts || res.data.default_shifts || []);
    } catch (error) {
      console.error('シフト取得エラー:', error);
      showAlert('エラー', 'シフトの取得に失敗しました');
    }
  };

  // 医師検索
  const searchDoctors = async () => {
    if (!selectedDiseaseId && !searchDate) {
      showAlert('入力エラー', '主疾患または日付を選択してください', 'warning');
      return;
    }
    
    if ((searchTimeFrom && !searchTimeTo) || (!searchTimeFrom && searchTimeTo)) {
      showAlert('入力エラー', '開始時刻と終了時刻を両方入力してください', 'warning');
      return;
    }
    
    setSearching(true);
    try {
      const params = {};
      if (selectedDiseaseId) params.disease_id = selectedDiseaseId;
      if (selectedSpecialtyId) params.specialty_id = selectedSpecialtyId;
      if (searchDate) params.date = searchDate;
      if (searchTimeFrom) params.time_from = searchTimeFrom;
      if (searchTimeTo) params.time_to = searchTimeTo;
      
      const res = await axiosInstance.get(`/api/medichat_doctor/post_consult_reviews/${sessionId}/next_appointment_options`, { params });
      const doctorsData = res.data.doctors || [];
      setDoctors(doctorsData);
      setHasSearched(true);
      
      // 担当医を自動選択（常に先頭に表示されているはず）
      if (currentDoctor) {
        selectDoctor(currentDoctor.id, currentDoctor.name);
      } else if (doctorsData.length > 0) {
        const firstDoctor = doctorsData[0];
        selectDoctor(firstDoctor.id, firstDoctor.name);
      }
    } catch (error) {
      console.error('医師検索エラー:', error);
      showAlert('エラー', '医師検索に失敗しました');
    } finally {
      setSearching(false);
    }
  };

  // 日付のトグル
  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // 空室状況の記号を取得
  const getAvailabilityBadge = (slotsCount) => {
    if (slotsCount >= 10) return { symbol: '◎', color: '#10b981', label: '' };
    if (slotsCount >= 4) return { symbol: '○', color: '#3b82f6', label: '' };
    if (slotsCount >= 1) return { symbol: '△', color: '#f59e0b', label: '' };
    return { symbol: '×', color: '#ef4444', label: '' };
  };

  // 予約確定
  const bookSlot = async (slotDatetime) => {
    if (!selectedDoctorId) {
      showAlert('入力エラー', '医師を選択してください', 'warning');
      return;
    }
    
    const dateObj = new Date(slotDatetime);
    const dateStr = dateObj.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const diseaseLabel = selectedDiseaseName || primaryDiseaseName || '（未選択）';
    const coverageLabel = selectedDiseaseInsuranceCovered === null
      ? '不明'
      : (selectedDiseaseInsuranceCovered ? '保険診療' : '自費診療');

    showConfirm(
      '確認',
      `担当医: ${selectedDoctorName}\n疾患: ${diseaseLabel}（${coverageLabel}）\n${dateStr}\n\n次回予約を作成しますか？`,
      async () => {
        try {
          const res = await axiosInstance.post(`/api/medichat_doctor/post_consult_reviews/${sessionId}/create_next_appointment`, {
            doctor_id: selectedDoctorId,
            slot_datetime: slotDatetime,
            primary_disease_id: selectedDiseaseId || undefined,
          });
          
          if (res.data.success) {
            showAlert('成功', '次回予約を作成しました！');
            // 成功時は少し遅延してからモーダルを閉じる
            setTimeout(() => {
              onClose();
            }, 1500);
          } else {
            showAlert('エラー', res.data.error || '予約に失敗しました');
          }
        } catch (error) {
          console.error('予約エラー:', error);
          showAlert('エラー', '予約処理中にエラーが発生しました');
        }
      }
    );
  };

  // 手動日時で予約
  const bookManual = () => {
    if (!manualDate || !manualTime) {
      showAlert('入力エラー', '日付と時刻を指定してください', 'warning');
      return;
    }
    // タイムゾーン情報（JST: +09:00）を付与してサーバーに送信
    const slotDatetime = `${manualDate}T${manualTime}:00+09:00`;
    bookSlot(slotDatetime);
  };

  // ショートカット適用
  const applyShortcut = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    setManualDate(toJstDateInputValue(d));
    setShowManualEntry(true);
    
    // 現在の時刻を5分単位で丸めてセット
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(Math.ceil(now.getMinutes() / 5) * 5 % 60).padStart(2, '0');
    setManualTime(`${hours}:${minutes}`);
  };

  if (!open) return null;

  return (
    <>
      <div className="mc-modal-overlay" onClick={onClose}>
        <div
          className="mc-modal-container mc-modal-container--next-appt"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mc-next-appt-modal-title"
        >
          <div className="mc-modal-header">
            <h2 id="mc-next-appt-modal-title" className="mc-modal-title">
              次回予約
              <span className="mc-modal-title-sub"> · {patientName}さん</span>
            </h2>
            <button type="button" className="mc-modal-close" onClick={onClose} aria-label="閉じる">×</button>
          </div>

          <div className="mc-modal-body">
            {loading ? (
              <div className="medichat-loading">読み込み中...</div>
            ) : (
              <>
                {/* 自分（現在の担当医） */}
                <div className="mc-next-appt-section mc-next-appt-section--doctor">
                  <h3 className="mc-section-title">担当医</h3>
                  <div className="mc-doctor-list">
                    {currentDoctor && (
                      <div
                        className={`mc-doctor-card mc-doctor-card--compact ${selectedDoctorId === currentDoctor.id ? 'active' : ''}`}
                        onClick={() => selectDoctor(currentDoctor.id, currentDoctor.name)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectDoctor(currentDoctor.id, currentDoctor.name);
                          }
                        }}
                      >
                        <div className="mc-doctor-name">{currentDoctor.name}</div>
                        <span className="mc-badge-current">今回の担当</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="mc-btn-ghost"
                    onClick={() => setShowOtherDoctors(v => !v)}
                  >
                    {showOtherDoctors ? '他の医師を閉じる' : '別の医師の空き枠を見る'}
                  </button>
                </div>

                {/* 検索フォーム＋医師一覧（トグル） */}
                {showOtherDoctors && (
                  <>
                    <div className="mc-next-appt-section">
                      <h3 className="mc-section-title">医師検索</h3>
                      {hasSearched && (doctors.filter(d => !currentDoctor || d.id !== currentDoctor.id).length === 0) && (
                        <div style={{color:'#dc2626', fontSize:'13px', marginBottom:'8px'}}>
                          ※該当する医師がいません
                        </div>
                      )}
                      <div className="mc-search-form">
                        <select
                          className="mc-input"
                          value={selectedDiseaseId || ''}
                          onChange={(e) => {
                            const val = e.target.value ? Number(e.target.value) : null;
                            setSelectedDiseaseId(val);
                            const selected = diseases.find(d => Number(d.id) === Number(val));
                            if (selected) {
                              setSelectedDiseaseName(selected.disease_name || selected.name || '');
                              setSelectedDiseaseInsuranceCovered(!!selected.insurance_covered);
                              if (selected.specialty_id) {
                                setSelectedSpecialtyId(String(selected.specialty_id));
                              }
                            } else {
                              setSelectedDiseaseName('');
                              setSelectedDiseaseInsuranceCovered(null);
                            }
                          }}
                          style={{ color: '#1f2937', WebkitTextFillColor: '#1f2937', background: '#ffffff' }}
                        >
                          <option value="">主疾患を選択</option>
                          {diseases.map(d => (
                            <option key={d.id} value={d.id}>{d.disease_name || d.name}</option>
                          ))}
                        </select>

                        <select
                          className="mc-input"
                          value={selectedSpecialtyId}
                          onChange={(e) => setSelectedSpecialtyId(e.target.value)}
                          style={{ color: '#1f2937', WebkitTextFillColor: '#1f2937', background: '#ffffff' }}
                        >
                          <option value="">診療科目を選択（任意）</option>
                          {specialties.map(s => (
                            <option key={s.id} value={s.id}>{s.specialty_name}</option>
                          ))}
                        </select>
                        
                        <input
                          type="date"
                          className="mc-input"
                          value={searchDate}
                          onChange={(e) => setSearchDate(e.target.value)}
                          min={toJstDateInputValue()}
                          style={{ color: '#1f2937', WebkitTextFillColor: '#1f2937', background: '#ffffff' }}
                        />
                        
                        <div style={{ display: 'flex', gap: '8px',marginBottom:'6px' }}>
                          <input
                            type="time"
                            className="mc-input"
                            placeholder="開始時刻（任意）"
                            value={searchTimeFrom}
                            onChange={(e) => setSearchTimeFrom(e.target.value)}
                            style={{ width:'48%', color: '#1f2937', WebkitTextFillColor: '#1f2937', background: '#ffffff' }}
                          />
                          <input
                            type="time"
                            className="mc-input"
                            placeholder="終了時刻（任意）"
                            value={searchTimeTo}
                            onChange={(e) => setSearchTimeTo(e.target.value)}
                            style={{ width:'48%', color: '#1f2937', WebkitTextFillColor: '#1f2937', background: '#ffffff' }}
                          />
                        </div>
                        
                        <button
                          className="mc-btn-primary"
                          onClick={searchDoctors}
                          disabled={searching}
                        >
                          {searching ? '検索中...' : '検索'}
                        </button>
                      </div>
                    </div>

                    <div className="mc-next-appt-section">
                      <h3 className="mc-section-title">医師一覧</h3>
                      <div className="mc-doctor-list">
                        {doctors.filter(d => !currentDoctor || d.id !== currentDoctor.id).map(doctor => (
                          <div
                            key={doctor.id}
                            className={`mc-doctor-card ${selectedDoctorId === doctor.id ? 'active' : ''}`}
                            onClick={() => selectDoctor(doctor.id, doctor.name)}
                          >
                            <div className="mc-doctor-name">{doctor.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* 空き枠（主操作：上に配置してスクロール量を減らす） */}
                <div className="mc-next-appt-section mc-next-appt-section--slots">
                  <h3 className="mc-section-title">空き枠を選ぶ</h3>
                  {selectedDoctorName ? (
                    <p className="mc-next-appt-lead">{selectedDoctorName} 医師</p>
                  ) : null}
                  <div className="mc-shift-container">
                    {shifts.length === 0 ? (
                      <div className="medichat-empty-state">この医師の予約可能枠がありません。下の「手動で日時を指定」もご利用ください。</div>
                    ) : (
                      shifts.map((shift, idx) => {
                        const isExpanded = expandedDates.has(shift.date);
                        const slotsCount = shift.slots.length;
                        const badge = getAvailabilityBadge(slotsCount);

                        return (
                          <div key={idx} className="mc-shift-date-group">
                            <div
                              className="mc-shift-date-header"
                              onClick={() => toggleDate(shift.date)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleDate(shift.date);
                                }
                              }}
                            >
                              <div className="mc-shift-date-left">
                                <span className="mc-shift-date-text">
                                  {new Date(shift.date).toLocaleDateString('ja-JP', {
                                    month: 'long',
                                    day: 'numeric',
                                    weekday: 'short'
                                  })}
                                </span>
                              </div>
                              <div className="mc-shift-date-right">
                                <span className="mc-shift-count">({slotsCount}枠)</span>
                                <span
                                  className="mc-availability-badge"
                                  style={{ color: badge.color }}
                                >
                                  {badge.symbol} {badge.label}
                                </span>
                                <span className="mc-accordion-arrow" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                  ▼
                                </span>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="mc-shift-slots">
                                {shift.slots.map((slot, sidx) => (
                                  <button
                                    type="button"
                                    key={sidx}
                                    className="mc-slot-btn"
                                    onClick={() => bookSlot(slot)}
                                  >
                                    {new Date(slot).toLocaleTimeString('ja-JP', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* 枠外の日時（折りたたみ：必要なときだけ開く） */}
                <details className="mc-next-appt-details">
                  <summary className="mc-next-appt-details-summary">
                    手動で日時を指定（ショートカット・カレンダー）
                  </summary>
                  <div className="mc-next-appt-details-body">
                    <p className="mc-next-appt-hint">表示されている空き枠以外の日時で入れる場合はこちら</p>
                    <div className="mc-next-appt-chips">
                      <button type="button" className="mc-btn-secondary" onClick={() => applyShortcut(1)}>明日</button>
                      <button type="button" className="mc-btn-secondary" onClick={() => applyShortcut(7)}>1週間後</button>
                      <button type="button" className="mc-btn-secondary" onClick={() => applyShortcut(14)}>2週間後</button>
                      <button type="button" className="mc-btn-secondary" onClick={() => applyShortcut(28)}>4週間後</button>
                      {showManualEntry ? (
                        <button
                          type="button"
                          className="mc-btn-secondary mc-btn-secondary--icon"
                          onClick={() => setShowManualEntry(false)}
                          aria-label="日時入力を閉じる"
                        >
                          ✕
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="mc-btn-secondary"
                          onClick={() => setShowManualEntry(true)}
                        >
                          カレンダーから指定
                        </button>
                      )}
                    </div>

                    {showManualEntry && (
                      <div className="mc-manual-entry-form">
                        <div className="mc-manual-entry-doctor">
                          担当: {selectedDoctorName} 医師
                        </div>
                        <div className="mc-manual-entry-row">
                          <div className="mc-manual-entry-field">
                            <label htmlFor="mc-next-appt-manual-date">予約日</label>
                            <input
                              id="mc-next-appt-manual-date"
                              type="date"
                              className="mc-input"
                              value={manualDate}
                              onChange={(e) => setManualDate(e.target.value)}
                              min={toJstDateInputValue()}
                            />
                          </div>
                          <div className="mc-manual-entry-field">
                            <label htmlFor="mc-next-appt-manual-time">開始時刻</label>
                            <input
                              id="mc-next-appt-manual-time"
                              type="time"
                              className="mc-input"
                              value={manualTime}
                              step="300"
                              onChange={(e) => setManualTime(e.target.value)}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="mc-btn-primary mc-btn-primary--manual"
                          onClick={bookManual}
                          disabled={!manualDate || !manualTime}
                        >
                          この日時で予約を確定
                        </button>
                      </div>
                    )}
                  </div>
                </details>
              </>
            )}
          </div>
        </div>
      </div>

      {/* AlertModal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        iconType={alertModal.iconType}
        onClose={closeAlert}
        onConfirm={alertModal.onConfirm}
      />
    </>
  );
};

export default NextAppointmentModal;
